/* ============================================
   EMAIL TEMPLATES — PRE-QUOTE
   Asset: email-tpl-inquiry.js
   CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-19)

   Emails sent before a quote exists. Loads after email-tpl-core.js and
   email-tpl-blocks.js; registers itself and knows nothing about the picker.
   ============================================ */
(function () {
  'use strict';

  var T = window.RQDTpl;
  var B = window.RQDTplBlocks;
  if (!T || !B) { console.error('[email-tpl] email-tpl-inquiry.js loaded before the kernel'); return; }

  var _INQUIRY_ITEM_CAP = 12;
  var TIMELINE_FINALLY_AFTER = 3; // CUSTOM_INQUIRYTHANKS_BY_CLAUDE - body paragraphs before the timeline that earn "Finally"

  /* CUSTOM_INQUIRYTHANKS_BY_CLAUDE (ONYX 3652ee794944, Ryan 2026-10-14) - HOW THE LEAD REACHED US, off the deal row's lead
     fields (worker lib/lead-fields.js). A rep's "+ New Lead" is a phone call unless it was built from an email; a sample a
     rep entered carries the channel the rep picked; every website form is a form; the chat bot and anything we cannot place
     is "inquiry". Exposed on RQDTplInquiry for the test. */
  var WEB_FORM_SOURCES = ['rfq', 'sample-request', 'weight-designer', 'bulk-quote', 'contact', 'contact-form', 'calculator'];
  function _contactMethod(dq) {
    if (!dq) return 'other';
    var src = String(dq.leadCaptureSource || '').toLowerCase();
    var formId = String(dq.leadFormId || '').toLowerCase();
    var channel = String(dq.sampleInquiryChannel || '').toLowerCase();
    if (formId === 'manual-sample-modal') return channel === 'phone' ? 'call' : (channel === 'email' ? 'email' : 'other');
    if (src === 'email-lead' || formId === 'email-lead-modal') return 'email';
    if (src === 'manual' || formId === 'manual-modal') return 'call';
    if (src === 'chatbot') return 'other';
    if (WEB_FORM_SOURCES.indexOf(src) !== -1) return 'form';
    return 'other';
  }

  /* ============================================
     Inquiry follow-up (Ryan 2026-08-19, rebuilt 2026-09-26)
     ============================================
     CUSTOM_INQUIRYFOLLOWUP_BY_CLAUDE (Ryan 2026-09-26) — it used to be "Inquiry follow-up (no
     answer)" and it was written about exactly one event, a missed call. Ryan: *"I don't like that
     it's static and not as dynamic as it could be... it could be for people who we call, who we
     don't call, who we can't connect with due to mailbox full, no vm set up, after hours, cut off
     mid-vm, etc."* So the email is now ONE skeleton, Ryan's own, and the call paragraph is the only
     part that moves with what the Activity card recorded:

       Hi {first},
       I'm reaching out in regards to your recent inquiry for the {products}. {Thank you for your call! / form submission! / email! / inquiry!}
       {the call paragraph - or nothing, when no call is logged or the attempt has gone stale}
       We're in the process of putting together a quote for you. As soon as we finalize the cost of
         shipping to {City, ST} then we'll email it over. {delivery question, only where it is smart}
       {Finally | Also}, I want to ensure that our lead times can accommodate your needs. What is your
         timeline...? {only when the form did not already ask}
       While you wait for your quote, please don't hesitate to reach out... email or call me at {phone}.
       Thanks again, {first}. Talk to you soon.

     ⛔ EVERY SENTENCE IS IN COPY BELOW, SO THE WORDING IS EDITABLE ON THE PORTAL. What stays in
     code is the CHOICE between sentences, which is a fact about the deal.

     It is once-per-customer, and it stops existing the moment a quote goes out. See the gates on
     the registry below. */
  var COPY = T.definePhrases('inquiry-follow-up', {
    /* The opener. Ryan: product names "with links to the product pages on the website, like we do
       in first quote to customer template". Up to three run inline; four or more become a
       bulleted list, and the thank-you moves BELOW the bullets because a colon introduces them. */
    'open.named':     "I'm reaching out in regards to your recent inquiry for the {PRODUCTS}. {THANKS}",
    'open.listed':    "I'm reaching out in regards to your recent inquiry for the following {CATEGORY}:",
    'open.unnamed':   "I'm reaching out in regards to your recent inquiry. {THANKS}",
    'thanksBelowList': '{THANKS}',

    /* CUSTOM_INQUIRYTHANKS_BY_CLAUDE (ONYX 3652ee794944, Ryan 2026-10-14) - "reaching out" said twice. The thank-you
       names how they reached us (`_contactMethod`, read off the deal row); "inquiry" when we cannot tell. */
    'thanks.call':  'Thank you for your call!',
    'thanks.form':  'Thank you for your form submission!',
    'thanks.email': 'Thank you for your email!',
    'thanks.other': 'Thank you for your inquiry!',

    /* A WEIGHT DESIGNER inquiry sent back its own design (Ryan 2026-09-26): the product name carries a
       link only once its product page is live; the design rides on "Click here". */
    'open.namedDesign':        "I'm reaching out in regards to your recent inquiry for the {PRODUCTS}. {CLICK_HERE} to see the design you made. {THANKS}",
    'thanksBelowList.design':  '{CLICK_HERE} to see the design you made. {THANKS}',
    /* …and when we have saved our own version too, both links sit in their own paragraph under a plain
       opener. {HERE_IS_A_LINK} is "here is a link" linked to THEIR design; {CLICK_HERE} to OURS. */
    'designs.both':     'As a reminder, {HERE_IS_A_LINK} to the weights you designed. Additionally {CLICK_HERE} to see a design we made for you, where we took your original design and made some modifications to it which we think enhanced the overall appearance.',
    'designs.oursOnly': '{CLICK_HERE} to see a design we made for you, where we took your original design and made some modifications to it which we think enhanced the overall appearance.',

    /* THE CALL PARAGRAPH — one sentence per thing the Activity card can record. The four "why no
       voicemail" reasons are Ryan's 2026-09-20 sentences from the first-quote email, joined onto
       "I gave you a call". */
    'call.connected':        'Thanks again for your time on the phone{WHEN}.',
    'call.voicemail':        'I gave you a call{WHEN} and left you a voicemail.',
    'call.tried':            "I gave you a call{WHEN} but we weren't able to connect.",
    'call.mailboxFull':      "I gave you a call{WHEN} but wasn't able to leave a message as the recording said your mailbox was full.",
    'call.noVmBox':          "I gave you a call{WHEN} but wasn't able to leave a voicemail. You likely have a missed call from me: {REP_PHONE}.",
    'call.noVmBoxNoPhone':   "I gave you a call{WHEN} but wasn't able to leave a voicemail, so you likely have a missed call from me.",
    'call.afterHours':       "I gave you a call{WHEN} but didn't leave a message as the recording stated it was outside of business hours.",
    'call.cutOff':           'I gave you a call{WHEN} and began to leave a voicemail, but I think the line dropped partway through.',
    'call.wrongNumber':      "I gave you a call but we weren't able to connect. Is the best number for you {PHONE}?",
    'call.wrongNumberNoPhone': "I gave you a call but we weren't able to connect. What is the best number to reach you at?",

    /* Its own paragraph, and only when the form did not already ask. Ryan: "if they've already
       provided this, we don't need to be redundant and ask them again."
       ⛔ There is no "Do you have any questions about our {products}" line any more (Ryan
       2026-09-26): the "While you wait" paragraph below already invites questions, so it was
       redundant. */
    'timeline':          'Finally, I want to ensure that our lead times can accommodate your needs. What is your timeline as far as when you need to receive the delivery?',
    /* CUSTOM_INQUIRYTHANKS_BY_CLAUDE (Ryan 2026-10-14) - "Finally" comes too early in the short version. "Also" unless
       TIMELINE_FINALLY_AFTER (3) body paragraphs come before it (the opener, a design or call paragraph, the quote). */
    'timeline.also':     'Also, I want to ensure that our lead times can accommodate your needs. What is your timeline as far as when you need to receive the delivery?',

    'quote.destination':   "We're in the process of putting together a quote for you. As soon as we finalize the cost of shipping to {DESTINATION} then we'll email it over.",
    'quote.noDestination': "We're in the process of putting together a quote for you. As soon as we finalize the cost of shipping then we'll email it over.",

    /* THE DELIVERY QUESTION, asked only where it is smart. Ryan on RW-1444-A: "it's most certainly
       a residential address, so we'd look dumb to ask about a loading dock or forklift on an
       inquiry like this." So there is deliberately NO residential sentence, and none for parcel
       orders or for a destination the Workstation already has answered. */
    'delivery.commercial':   'Do you have a loading dock or a forklift on site? It will affect the shipping rate.',
    'delivery.unclassified': 'Is there a loading dock or a forklift at the delivery address?',
    'delivery.noAddress':    'Can you please confirm the shipping address, and whether there is a loading dock or a forklift on site?',

    /* CUSTOM_INQUIRYSAMPLE_BY_CLAUDE (AIOPS 0d681a4b5eb7, Ryan 2026-10-11) — THE SAME EMAIL TO A SAMPLE
       LEAD. Ryan, on RW-1494-A: when this goes first to somebody who asked for samples, it should say
       the samples are on their way and the quote is coming, in the first sample email's spirit, and
       check the address once for both: "The address I have on file for both your samples and a quote
       is XYZ. Is this correct?" A commercial address still gets the loading-dock question. */
    'open.sample.named':   "I'm reaching out in regards to your recent sample request for the {PRODUCTS}. {THANKS}",
    'open.sample.listed':  "I'm reaching out in regards to your recent sample request for the following {CATEGORY}:",
    'open.sample.unnamed': "I'm reaching out in regards to your recent sample request. {THANKS}",
    'sample.quote.destination':   "We're going to get your {SAMPLE_NOUN} sent off and begin working on a quote for you too. As soon as we finalize the cost of shipping to {DESTINATION}, we'll email it over.",
    'sample.quote.noDestination': "We're going to get your {SAMPLE_NOUN} sent off and begin working on a quote for you too. As soon as we finalize the cost of shipping, we'll email it over.",
    'sample.address':   'The address I have on file for both your {SAMPLE_NOUN} and your quote is {ADDRESS}. Is this correct?',
    'sample.noAddress': "Could you send over the address you'd like your {SAMPLE_NOUN} and your quote shipped to?",

    'meantime.phone':   "While you wait for your quote, please don't hesitate to reach out if you have any questions. Feel free to email or call me at {REP_PHONE}.",
    'meantime.noPhone': "While you wait for your quote, please don't hesitate to reach out if you have any questions. Feel free to email or call me any time.",

    'signOff.named':   'Thanks again, {FIRST}. Talk to you soon.',
    'signOff.unnamed': 'Thanks again! Talk to you soon.',
  }, {
    PRODUCTS:     'the product names, each linked to its page: "Rally Rubber Roll and Power Rack"',
    CATEGORY:     'what the bulleted list is: "flooring products", "gym equipment", "custom weights"',
    CLICK_HERE:   '"Click here" (or "click here"), linked to a design',
    HERE_IS_A_LINK: '"here is a link", linked to the design the customer made',
    PHONE:        "the customer's phone number exactly as it is on file",
    REP_PHONE:    'your phone number, from your rep profile',
    DESTINATION:  'where it ships: "Taft, CA"',
    SAMPLE_NOUN:  '"sample" or "samples", by how many they asked for',
    ADDRESS:      'the delivery address on file, in bold: "12 Main St, Yorktown Heights, NY 10598"',
    FIRST:        "the customer's first name",
    THANKS:       'the thank-you for how they reached us: "Thank you for your call!" / "...form submission!" / '
                  + '"...email!" / "...inquiry!" when it cannot be told',
    WHEN:         'when the call was, e.g. " yesterday". Carries its own leading space and is often '
                  + 'empty, so do not put a space in front of it.',
  });

  /* ============================================
     WHAT THE CUSTOMER ASKED ABOUT
     ============================================ */

  /* CUSTOM_INQUIRYFOLLOWUP_BY_CLAUDE (Ryan 2026-09-26) — A WEIGHT DESIGNER LINE, NAMED AND LINKED.
     A WD lead carries its set as `designItems` ({label: 'Bumper Plates', kind: 'Competition
     Rubber'}), not as Shopify lines, so the kernel's product reader found nothing and the email
     named no product at all.

     ⛔ THE LINK FINDS THE PRODUCT PAGE BY ITSELF ONCE IT IS LIVE. Ryan: "we'll need this to
     recognize when the product pages are live." The product-mention index holds PUBLISHED
     products only, so the day a Ballast Strength custom page is published it enters the index
     and this starts linking to it — no code change. Until then the link is the customer's OWN
     saved design, which is the most specific page there is about what they asked for.
     ⚠️ THE MATCH IS NARROW ON PURPOSE: vendor Ballast Strength (the WD's house brand), the word
     "custom" in the title or handle (so a stock blank plate never stands in for their custom
     set), and every word of the kind and the label present in the title. */
  function _words(s) {
    return String(s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)
      .map(function (w) { return w.length > 3 ? w.replace(/s$/, '') : w; });
  }
  function _wdProductPage(di, index) {
    var need = _words((di.kind || '') + ' ' + (di.label || ''));
    if (!need.length) return null;
    var hit = (Array.isArray(index) ? index : []).find(function (p) {
      if (!p || !p.u || !/ballast/i.test(String(p.v || ''))) return false;
      if (!/custom/i.test(String(p.t || '') + ' ' + String(p.h || ''))) return false;
      var have = _words(p.t);
      return need.every(function (w) { return have.indexOf(w) !== -1; });
    });
    return hit ? hit.u : null;
  }
  function _designLink(dq) {
    var r = (dq && dq.leadResponses) || {};
    var u = String(r['Design link'] || (dq && dq.leadSourceUrl) || '').trim();
    return /weight-designer\?d=/.test(u) ? u : '';
  }
  function _wdProducts(dq, index, warnings) {
    var list = (dq && Array.isArray(dq.leadDesignItems)) ? dq.leadDesignItems : [];
    var design = _designLink(dq);
    var out = [];
    list.forEach(function (di) {
      if (!di || !di.label) return;
      var title = 'Custom ' + [di.kind, di.label].filter(Boolean).join(' ');
      var page = _wdProductPage(di, index);
      /* Ryan 2026-09-26: the NAME links to the product page once it is live, and to nothing before;
         their design is linked by its own "Click here" sentence instead. */
      if (!page) {
        warnings.push(title + ' has no live product page yet, so the name goes in without a link'
          + (design ? ' and "Click here" links their design.' : '.'));
      }
      out.push({ title: title, url: page || '', wd: true, wdLabel: di.label });
    });
    return out;
  }

  /* The noun for the bulleted list's lead-in ("the following flooring products:"). */
  function _category(items) {
    if (items.length && items.every(function (it) { return it.wd; })) return 'custom weights';
    return T.categoryPhrase(items);
  }

  /* ============================================
     DID THE FORM ALREADY ASK ABOUT TIMING?
     ============================================
     Most of the web forms ask, under different names: the Weight Designer writes "Timeline", the
     add-to-quote form "Install Timeline", CFA handoffs carry `discovery.timeline`. Any non-empty
     answer counts - "Just Exploring" is an answer, and asking again reads as not having read it. */
  var _TIMELINE_KEY = /time\s*line|time\s*frame|how soon|when do you need|install date/i;
  var _NOT_GIVEN = /^\s*(\(not given\)|n\/?a|none|-+)?\s*$/i;
  function _statedTimeline(details, dq) {
    var sources = [
      dq && dq.leadResponses,
      details && details.workstationMeta && details.workstationMeta.leadContext
        && details.workstationMeta.leadContext.responses,
    ];
    for (var i = 0; i < sources.length; i++) {
      var r = sources[i];
      if (!r || typeof r !== 'object') continue;
      var keys = Object.keys(r);
      for (var k = 0; k < keys.length; k++) {
        if (_TIMELINE_KEY.test(keys[k]) && !_NOT_GIVEN.test(String(r[keys[k]] || ''))) return String(r[keys[k]]).trim();
      }
    }
    var wmc = details && details.workstationMeta && details.workstationMeta.customer;
    if (wmc && wmc.timeline && !_NOT_GIVEN.test(wmc.timeline)) return String(wmc.timeline).trim();
    var disc = dq && dq.discovery;
    if (disc && disc.timeline && !_NOT_GIVEN.test(disc.timeline)) return String(disc.timeline).trim();
    return '';
  }

  /* ============================================
     WHERE IT SHIPS
     ============================================
     Ship To when it holds anything, else Sold To — the same order the worker's address lookup
     uses. The Workstation's copy first, then the dashboard row (a lead that was never made into
     a deal has only that). */
  function _address(details, dq) {
    var c = (details && details.workstationMeta && details.workstationMeta.customer) || null;
    if (c) {
      var ship = { street: c.shipStreet, street2: c.shipStreet2, city: c.shipCity, state: c.shipState, zip: c.shipZip };
      var sold = { street: c.street, street2: c.street2, city: c.city, state: c.state, zip: c.zip };
      var a = (ship.street || ship.city || ship.zip) ? ship : sold;
      if (a.street || a.city || a.zip) { a.statedType = c.customerType || ''; return a; }
    }
    var sa = details && details.quote && details.quote.shippingAddress;
    if (sa && (sa.address1 || sa.city || sa.zip)) {
      return { street: sa.address1, street2: sa.address2, city: sa.city, state: sa.provinceCode, zip: sa.zip };
    }
    if (dq) {
      var st = dq.shipTo;
      if (st && (st.street || st.city || st.zip)) {
        return { street: st.street, street2: st.street2, city: st.city, state: st.state, zip: st.zip };
      }
      if (dq.customerStreet || dq.city || dq.zip) {
        return { street: dq.customerStreet, street2: dq.customerStreet2, city: dq.city, state: dq.state, zip: dq.zip };
      }
    }
    return null;
  }

  /* "{City, ST}" for the quote sentence, from the first source that has a city or a state. Kept
     apart from _address on purpose: the Workstation often holds only a ZIP while the draft order
     beside it has the state, and the destination should not go blank for that. */
  function _destination(details, dq) {
    var c = (details && details.workstationMeta && details.workstationMeta.customer) || {};
    var sa = (details && details.quote && details.quote.shippingAddress) || {};
    var st = (dq && dq.shipTo) || {};
    var shipSet = !!(c.shipStreet || c.shipCity || c.shipZip);
    var cands = [
      shipSet ? { city: c.shipCity, provinceCode: c.shipState } : null,
      shipSet ? null : { city: c.city, provinceCode: c.state },
      { city: sa.city, provinceCode: sa.provinceCode },
      { city: st.city, provinceCode: st.state },
      dq ? { city: dq.city, provinceCode: dq.state } : null,
    ];
    for (var i = 0; i < cands.length; i++) {
      var a = cands[i];
      if (a && (a.city || a.provinceCode)) return T.shipDestination(a);
    }
    return '';
  }

  /* ============================================
     THE LATEST CALL, AND WHAT IT LETS US SAY
     ============================================
     The most recent logged call of ANY outcome — the one the customer might remember. */
  function _latestCall(deals) {
    var best = null;
    (Array.isArray(deals) ? deals : []).forEach(function (q) {
      ((q && Array.isArray(q.noteEntries)) ? q.noteEntries : []).forEach(function (n) {
        if (!n || !n.ts) return;
        var types = Array.isArray(n.types) && n.types.length ? n.types : (n.type ? [n.type] : []);
        if (types.indexOf('call') === -1) return;
        if (!best || new Date(n.ts) > new Date(best.ts)) best = n;
      });
    });
    return best;
  }

  /* Which call sentence, or null for none. The staleness rules are the house ones (see
     callReference in email-tpl-blocks.js): a missed attempt is only worth mentioning for two
     days, a conversation for about a week.
     ⛔ THE WRONG-NUMBER SENTENCE IGNORES BOTH. It carries no date, and asking for the right number
     stays worth doing however long ago we found the wrong one. */
  var _REASON_KEYS = { 'mailbox-full': 'mailboxFull', 'after-hours': 'afterHours', 'cut-off': 'cutOff' };
  function _callParagraph(call, phone, repPhone, warnings) {
    if (!call) return null;
    var w = T.whenPhrase(call.ts, T.now());
    var WHEN = w.phrase ? ' ' + w.phrase : '';
    var outcome = String(call.callOutcome || '');
    if (outcome !== 'no-answer') {
      return w.recent ? COPY['call.connected']({ WHEN: WHEN }) : null;
    }
    var detail = String(call.callVm || '') === 'none' ? String(call.callDetail || '') : '';
    if (detail === 'wrong-number') {
      if (phone) return COPY['call.wrongNumber']({ PHONE: phone });
      warnings.push('The call was logged as a wrong number, but there is no phone on this deal, so the email asks for their number instead of reading one back.');
      return COPY['call.wrongNumberNoPhone']({});
    }
    if (!w.fresh) {
      warnings.push('The last call was more than two days ago, so the email does not mention it.');
      return null;
    }
    if (String(call.callVm || '') === 'left') return COPY['call.voicemail']({ WHEN: WHEN });
    if (detail === 'no-vm-box') {
      if (repPhone) return COPY['call.noVmBox']({ WHEN: WHEN, REP_PHONE: repPhone });
      warnings.push('No phone number is set on your rep profile, so the call line says they have a missed call without giving the number.');
      return COPY['call.noVmBoxNoPhone']({ WHEN: WHEN });
    }
    if (_REASON_KEYS[detail]) return COPY['call.' + _REASON_KEYS[detail]]({ WHEN: WHEN });
    if (!call.callVm) {
      warnings.push('That call was logged before the voicemail question existed, so the email says you could not connect without mentioning a voicemail.');
    }
    return COPY['call.tried']({ WHEN: WHEN });
  }

  function _buildInquiryFollowUp(ctx) {
    var ref = String((ctx && ctx.ref) || '').trim();
    var dq = T.dealByRef(ref);
    var isLead = !!(dq && (dq.isLead || dq.status === 'new_lead'));
    return Promise.all([
      T.loadDetails(ref),
      T.loadMentionIndex(),
      T.ensureChain(ref),        // so a call logged on a sibling revision is visible
      T.loadRepSettings(),
    ]).then(function (res) {
      var details = res[0] || null;
      var index = res[1] || [];
      var settings = res[3] || {};
      dq = T.dealByRef(ref) || dq;
      var warnings = [];
      var addr = _address(details, dq);
      /* The address lookup is the slowest read here (a live web search), so it runs beside the
         rest of the build rather than in front of it — but it needs the address first. */
      var addrP = T.classifyAddress(ref, addr || null);

      var first = B.customerFirstName(details, dq, ctx);
      var phone = T.customerPhone(details, dq);
      var repPhone = String(settings.repPhone || '').trim();
      if (phone && !T.phoneLooksReal(phone)) {
        warnings.push('The phone on this deal, ' + phone + ', does not look like a real US number. If you could not reach them on it, log the call as Wrong number and the email will ask them for the right one.');
      }

      /* ---- the products ---- */
      var items = T.inquiryProducts(details, index);
      if (!items.length) items = _wdProducts(dq, index, warnings);
      /* CUSTOM_INQUIRYSAMPLE_BY_CLAUDE — a sample lead's picks, read off the deal row by name. */
      var isSampleLead = !!(dq && ((Array.isArray(dq.leadSamples) && dq.leadSamples.length)
        || dq.isSample || String(dq.leadCaptureSource || '') === 'sample-request'));
      if (!items.length && typeof T.sampleProducts === 'function') items = T.sampleProducts(dq, index);
      var sampleCount = (dq && Array.isArray(dq.leadSamples))
        ? dq.leadSamples.filter(function (s) { return s && String(s).trim(); }).length : 0;
      var sampleNoun = (sampleCount > 1 || (!sampleCount && items.length > 1)) ? 'samples' : 'sample';
      var op = isSampleLead ? 'open.sample.' : 'open.';
      if (items.length > _INQUIRY_ITEM_CAP) {
        warnings.push('This deal has ' + items.length + ' items on it, so the list was cut to the first '
          + _INQUIRY_ITEM_CAP + '. Add the rest by hand if they matter.');
        items = items.slice(0, _INQUIRY_ITEM_CAP);
      }
      var unlinked = items.filter(function (it) { return !it.url && !it.wd; }).map(function (it) { return it.title; });
      if (unlinked.length) {
        warnings.push(unlinked.map(function (t) { return '"' + t + '"'; }).join(', ')
          + (unlinked.length === 1 ? ' is' : ' are') + ' not in the product index, so '
          + (unlinked.length === 1 ? 'it went' : 'they went') + ' in without a link.');
      }
      var many = items.length >= 4;
      var method = _contactMethod(dq); // CUSTOM_INQUIRYTHANKS_BY_CLAUDE
      var thanks = COPY['thanks.' + method]({});
      if (method === 'other') warnings.push('Could not tell how this lead reached us, so the email thanks them for their inquiry.');

      /* CUSTOM_INQUIRYFOLLOWUP_BY_CLAUDE (Ryan 2026-09-26) — OUR IMPROVED DESIGN, WHEN WE MADE ONE.
         Ryan: "yes, add it automatically." It is the composer's own "Link to the version we made
         for them" snippet (email-tpl-snippets.js), built here rather than copied, so the sentence
         and its link rules live in ONE place: newest of our versions, its own solo link, never the
         chain link. ⚠️ The snippet REFUSES by throwing when no version of ours exists, and that
         refusal is the normal case here, so it resolves to nothing rather than to a warning. */
      /* Ryan 2026-09-26 reworded both shapes, so the links are built here in his words, from the
         snippets file's own lookup (`T.wdDesigns`, `T.wdLink`): newest of OUR versions by its solo
         link, never the chain link. Their design: the lead's own design link, else the share on the
         deal's linked files. */
      var wdd = (typeof T.wdDesigns === 'function') ? T.wdDesigns({ ref: ref }) : null;
      var theirsUrl = _designLink(dq)
        || (wdd && wdd.theirs && wdd.theirs[0] && typeof T.wdLink === 'function' ? T.wdLink(wdd.theirs[0].designId) : '');
      var oursUrl = (wdd && wdd.ourShareId && typeof T.wdLink === 'function') ? T.wdLink(wdd.ourShareId) : '';

      return addrP.then(function (cls) {
        var paras = [B.greeting(first)];
        var bodyBlocks = 0; // CUSTOM_INQUIRYTHANKS_BY_CLAUDE - paragraphs before the timeline (the bulleted opener counts once)
        /* "Click here" on the opener only when their design is the ONLY design we are sending; with
           ours as well, both go in the paragraph below and the opener stays plain. */
        var clickTheirs = !!theirsUrl && !oursUrl;

        /* ---- the opener ---- */
        if (!items.length) {
          paras.push(COPY[op + 'unnamed']({ THANKS: thanks }));
          warnings.push('No products found on this deal, so the opening line does not name what they asked about.');
        } else if (many) {
          paras.push(COPY[op + 'listed']({ CATEGORY: _category(items) }));
          B.bulletProducts(items).forEach(function (b) { paras.push(b); });
          paras.push(clickTheirs
            ? COPY['thanksBelowList.design']({ CLICK_HERE: [{ t: 'Click here', link: theirsUrl }], THANKS: thanks })
            : COPY['thanksBelowList']({ THANKS: thanks }));
        } else if (clickTheirs) {
          paras.push(COPY['open.namedDesign']({ PRODUCTS: T.joinRuns(items), CLICK_HERE: [{ t: 'Click here', link: theirsUrl }], THANKS: thanks }));
        } else {
          paras.push(COPY[op + 'named']({ PRODUCTS: T.joinRuns(items), THANKS: thanks }));
        }
        bodyBlocks++; // the opener

        /* ---- the version we made for them, right under what they asked about ---- */
        if (oursUrl) {
          paras.push(theirsUrl
            ? COPY['designs.both']({ HERE_IS_A_LINK: [{ t: 'here is a link', link: theirsUrl }], CLICK_HERE: [{ t: 'click here', link: oursUrl }] })
            : COPY['designs.oursOnly']({ CLICK_HERE: [{ t: 'Click here', link: oursUrl }] }));
          warnings.push('Your improved design is saved on this deal, so the email links it and theirs under the opening line.');
          bodyBlocks++;
        }

        /* ---- the call ---- */
        var call = _latestCall(T.dealSet(ref));
        var callPara = _callParagraph(call, phone, repPhone, warnings);
        if (callPara) { paras.push(callPara); bodyBlocks++; }

        /* ---- the timeline, asked below the quote paragraph unless the form already did ---- */
        var stated = _statedTimeline(details, dq);
        if (stated) {
          warnings.push('They already gave a timeline on the form ("' + stated + '"), so the email does not ask for one.');
        }

        /* ---- the quote, and the one delivery question worth asking ---- */
        var dest = _destination(details, dq);
        var quote = dest ? COPY['quote.destination']({ DESTINATION: dest }) : COPY['quote.noDestination']({});
        var ask = null;
        var mode = T.shipMode(items);
        var confirmed = T.confirmedDelivery(details);
        var kind = (cls && cls.kind) || 'unknown';
        /* The customer's own word counts only as a reason NOT to ask: the lookup overrules the
           form when it has a verdict (Ryan has watched "commercial" turn out to be a farm), but
           asking someone who ticked "residential" about their loading dock is the exact look
           Ryan does not want. */
        /* ⚠️ THE LOOKUP MAY KNOW A STREET WE DO NOT. It reads the deal on the worker, so an
           address that never reached this page still comes back in `cls.address` — and asking the
           customer to "confirm the shipping address" we already have reads as having lost it.
           A line that does not open with a street number ("FL 33470", a bare ZIP) is not one. */
        var hasStreet = !!((addr && addr.street) || /^\s*\d+\S*\s+[A-Za-z]/.test(String((cls && cls.address) || '')));
        var saidResidential = /resid/i.test(String((addr && addr.statedType) || (cls && cls.statedType) || ''));
        if (mode === 'parcel') {
          warnings.push('Every item has a known weight under the parcel limit, so the email does not ask about a loading dock.');
        } else if (confirmed) {
          warnings.push('The Workstation already records ' + confirmed.label + ' at this destination, so the email does not ask about one.');
        } else if (!hasStreet) {
          ask = COPY['delivery.noAddress']({});
          warnings.push('No delivery address on this deal, so the email asks them for it.');
        } else if (kind === 'residential' || (kind === 'unknown' && saidResidential)) {
          warnings.push('The delivery address looks residential, so the email does not ask about a loading dock or forklift.');
        } else if (kind === 'commercial') {
          ask = COPY['delivery.commercial']({});
        } else {
          ask = COPY['delivery.unclassified']({});
        }
        if (cls && cls.address !== undefined) {
          warnings.push('Delivery address (' + (cls.source || 'on file') + '): ' + (cls.address || 'none') + '. '
            + (cls.cached ? 'Looked it up earlier: ' : (cls.searched ? 'Looked it up: ' : ''))
            + (cls.reason || '') + (cls.found ? ' Found: ' + cls.found + '.' : '') + ' Read it as ' + kind + '.');
          if (cls.confidence === 'low' && kind !== 'unknown') {
            warnings.push('That read was low confidence, so check the shipping paragraph before sending.');
          }
        } else if (hasStreet) {
          warnings.push('The delivery address could not be looked up, so the email asks about a loading dock rather than assuming.');
        }
        if (!dest) warnings.push('No city or state for the delivery, so the quote line does not say where it ships.');
        /* Ryan's order (2026-09-26): the quote statement with the delivery ask beside it (it is
           about the shipping the statement just mentioned), then the timeline as its own paragraph. */
        if (isSampleLead) {
          /* CUSTOM_INQUIRYSAMPLE_BY_CLAUDE (Ryan 2026-10-11) — the samples go out, the quote follows,
             and the address is checked once for both. The no-address question replaces the generic
             one; a commercial or unclassified address still gets the loading-dock question. */
          var sq = dest ? COPY['sample.quote.destination']({ SAMPLE_NOUN: sampleNoun, DESTINATION: dest })
                        : COPY['sample.quote.noDestination']({ SAMPLE_NOUN: sampleNoun });
          var addrText = '';
          if (addr && addr.street) {
            var cityLine = [addr.city, [addr.state, addr.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
            addrText = [addr.street, addr.street2, cityLine].filter(function (x) { return x && String(x).trim(); }).join(', ');
          } else if (hasStreet) {
            addrText = String((cls && cls.address) || '').trim();
          }
          var addrS = addrText
            ? COPY['sample.address']({ SAMPLE_NOUN: sampleNoun, ADDRESS: [{ t: addrText, b: true }] })
            : COPY['sample.noAddress']({ SAMPLE_NOUN: sampleNoun });
          var sampleAsk = (hasStreet && ask) ? ask : null;
          var sp = sq.concat([{ t: ' ' }], addrS);
          paras.push(sampleAsk ? sp.concat([{ t: ' ' }], sampleAsk) : sp);
          bodyBlocks++;
          warnings.push('This is a sample lead, so the email says the ' + sampleNoun + (sampleNoun === 'sample' ? ' is' : ' are') + ' on the way and asks them to confirm the address for both.');
        } else {
          paras.push(ask ? quote.concat([{ t: ' ' }], ask) : quote);
          bodyBlocks++;
        }
        if (!stated) paras.push(bodyBlocks >= TIMELINE_FINALLY_AFTER ? COPY['timeline']({}) : COPY['timeline.also']({}));

        /* ---- the door ---- */
        if (repPhone) {
          paras.push(COPY['meantime.phone']({ REP_PHONE: repPhone }));
        } else {
          paras.push(COPY['meantime.noPhone']({}));
          warnings.push('No phone number is set on your rep profile, so the email does not offer one.');
        }
        paras.push(first ? COPY['signOff.named']({ FIRST: first }) : COPY['signOff.unnamed']({}));

        if (!details && !isLead) warnings.push('Could not read ' + (ref || 'the deal') + ', so the products and the ship-to may be missing.');

        /* ⛔ The subject NAMES THE THREAD every later email replies into, so it is set here,
           on the first email of the conversation, and never rewritten downstream. */
        var fullName = B.customerFullName(details, dq, ctx);
        if (!fullName) warnings.push('No customer name on this deal, so the subject line starts with the category.');
        var subject = B.quoteSubject(fullName, items, { ref: ref, sample: isSampleLead });   // CUSTOM_INQUIRYSUBJECT_BY_CLAUDE

        return { paragraphs: paras, warnings: warnings, subject: subject };
      });
    });
  }

  window.RQDTplInquiry = { contactMethod: _contactMethod, TIMELINE_FINALLY_AFTER: TIMELINE_FINALLY_AFTER }; // CUSTOM_INQUIRYTHANKS_BY_CLAUDE

  T.register({
    /* Registered FIRST across the whole system - Ryan: this one "would appear higher in
       the pecking order than sending them the first quote". It is also the only template
       live on a brand-new inquiry, so the top row is the one a rep can actually use.
       ⛔ THE ID STAYS 'inquiry-follow-up' through the 2026-09-26 rename: it is the key the send
       ledger records, so changing it would re-open this email for every customer who has had it. */
    id: 'inquiry-follow-up',
    icon: '📞',
    label: 'Inquiry follow-up',
    hint: 'What they asked about, whatever happened on the call, their timeline if the form did not ask, and the quote on its way',
    build: _buildInquiryFollowUp,
    gate: function (st) {
      if (st.quoted) return 'The quote has already gone out, so this one is no longer relevant.';
      if (st.wasSent('inquiry-follow-up')) return 'Already sent to this customer.';
      return null;
    },
  });
})();
